import { Routes } from '@angular/router';
import { buildProfileEditChildren } from './profile/constants/profile-sections';
import { ProfileCompletenessStore } from './profile/stores/profile-completeness.store';
import { VendorProfileStore } from './profile/stores/vendor-profile.store';

/**
 * Route area situs VENDOR (bukan menu admin `vendor-management`). Di-spread di
 * `main-layout.routes.ts` TANPA segmen prefix karena menu BE mengarah langsung ke
 * `vendor/profile`, `vendor/expired_doc_status`, dst.
 *
 * `path` tiap child HARUS cocok dengan `navigable.path` dari navigation_tree/MAIN (sidebar memakai
 * path itu apa adanya sebagai `routerLink`) — termasuk underscore-nya. Jangan diubah jadi
 * kebab-case.
 *
 * Bentuk `path: ''` + `children`: ketiga tab adalah URL SEJAJAR, tapi shell (header identitas +
 * tab bar + footer Submit) harus BERTAHAN saat pindah tab supaya `GET current_user` cuma
 * sekali. Route induk yang tetap aktif memberi keduanya sekaligus.
 *
 * `VendorProfileStore` di-provide DI SINI (bukan singleton root): datanya milik halaman ini dan
 * ikut mati saat user meninggalkannya. `ProfileCompletenessStore` ikut di sini — BUKAN di
 * `providers` komponen tab — karena `allComplete()`-nya menjadi gerbang tombol Submit Submission
 * di shell, jadi harus tetap hidup saat user berada di tab lain
 * (plans/vendors/profile/PLAN-PROFILE-TAB.md §D9).
 *
 * Lihat plans/vendors/profile/PLAN.md §5.
 */
export const vendorRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./profile/pages/vendor-profile/vendor-profile').then((c) => c.VendorProfileComponent),
    providers: [VendorProfileStore, ProfileCompletenessStore],
    children: [
      {
        path: 'profile',
        loadComponent: () =>
          import('./profile/pages/tabs/profile-tab/profile-tab').then((c) => c.ProfileTabComponent),
        title: 'vendorProfile.title',
      },
      {
        path: 'expired_doc_status',
        loadComponent: () =>
          import('./profile/pages/tabs/expired-doc-status-tab/expired-doc-status-tab').then(
            (c) => c.ExpiredDocStatusTabComponent,
          ),
        title: 'vendorProfile.tab.expiredDocStatus',
      },
      {
        path: 'comment_history',
        loadComponent: () =>
          import('./profile/pages/tabs/comment-history-tab/comment-history-tab').then(
            (c) => c.CommentHistoryTabComponent,
          ),
        title: 'vendorProfile.tab.commentHistory',
      },
    ],
  },

  /**
   * Shell "Perbarui Dokumen" — `/vendor/profile/<section>` (15 halaman dokumen profil).
   *
   * SAUDARA shell tab di atas, bukan anaknya: chrome-nya berbeda (tanpa hero card, tanpa tab bar)
   * dan ia menguasai tingginya sendiri supaya sidebar & kolom kanan bisa menggulir sendiri-sendiri.
   * Menaruhnya sebagai anak berarti membawa serta hero + tab bar yang tidak diminta.
   *
   * Store shell tab (`VendorProfileStore`, `ProfileCompletenessStore`) TIDAK di-provide di sini:
   * halaman ini belum menyentuh HTTP sama sekali.
   *
   * URUTAN PENTING — entry ini WAJIB DI BAWAH entry `path: ''`. Bukan preferensi; menaikkannya
   * MERUSAK halaman tab, dan rusaknya tidak terlihat sebagai error:
   *   - `/vendor/profile` → dicocokkan entry `''` (anak `profile`) → halaman TAB. Langsung, tanpa
   *     mundur.
   *   - `/vendor/profile/organizations` → entry `''` mencocokkan `profile` lalu menyisakan segmen
   *     `organizations` yang tak punya anak → NoMatch → router MUNDUR ke entry berikutnya
   *     (`Recognizer.processSegment` menangkap NoMatch lalu `continue`) → entry ini yang cocok.
   *     Mundurnya router memang perilaku yang sudah diandalkan project ini — lihat komentar
   *     `...vendorRoutes` di `main-layout.routes.ts`.
   *
   * Kalau entry ini dinaikkan ke ATAS, `/vendor/profile` TIDAK mundur ke halaman tab: ia cocok di
   * sini, dan karena tak ada sisa segmen `Recognizer` menerimanya lewat `noLeftoversInUrl()` →
   * shell ini dirender dengan `<router-outlet />` KOSONG. Halaman tab lenyap tanpa satu pun error.
   */
  {
    path: 'profile',
    loadComponent: () =>
      import('./profile/pages/profile-edit/profile-edit').then((c) => c.ProfileEditComponent),
    // Mode & judul dibaca `provideProfileEditContext()` dari route INI (bukan dari route anak).
    // Ditulis eksplisit walau nilainya sama dengan default factory-nya: route kembarannya di
    // `vendor-management.routes.ts` mengisi `review`, dan dua route yang mengisi field yang sama
    // lebih mudah dibandingkan daripada satu yang mengisi dan satu yang mengandalkan default.
    data: { profileEditMode: 'edit', profileEditTitleKey: 'vendorProfile.edit.title' },
    children: buildProfileEditChildren(),
  },
];
